import React, { createContext, useContext, useState, useEffect } from "react";

import Strings from "../constants/Strings";
import { decodeToken } from "react-jwt";

import api from "../services/api";
import { toast } from "react-toastify";
import { User, AuthContextType, AuthProviderProps } from "../types";

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const getUser = (): User | null => {
    const storedToken = localStorage.getItem(Strings.token_jwt);

    if (storedToken) {
      const decodedToken = decodeToken<User>(storedToken);

      return decodedToken;
    }
    return null;
  };

  useEffect(() => {
    setLoading(true);
    try {
      const decodedToken = getUser();
      setUser(decodedToken);
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post("/professionals/login", {
        email,
        password,
      });
      const token = response.data.token;

      // We might need to decode the token to set the user state immediately,
      // or just trust the next effect cycle/reload.
      // Ideally set state here too.
      const decoded = decodeToken<User>(token);
      setUser(decoded);

      localStorage.setItem(Strings.token_jwt, token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      if (err?.response?.data?.error) {
        toast.error(err.response.data.error);
      }
    }
  };

  const logout = () => {
    localStorage.removeItem(Strings.token_jwt);
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prevUser) => {
      if (!prevUser) return prevUser;
      return { ...prevUser, ...updates };
    });
  };

  const refreshToken = (newToken: string) => {
    try {
      const decoded = decodeToken<User>(newToken);
      setUser(decoded);
      localStorage.setItem(Strings.token_jwt, newToken);
      api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      return true;
    } catch (error) {
      console.error("Erro ao atualizar token:", error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isLogged: user !== null,
        login,
        logout,
        getUser,
        updateUser,
        refreshToken,
      }}
    >
      {loading ? null : children}
    </AuthContext.Provider>
  );
};

// Crie um hook personalizado para acessar o contexto de autenticação
export const useAuth = () => useContext(AuthContext);
